// @api/integrations.js
import session from '@api/session'
import { APIEndpoint } from '@api/_version'

export default {
  /**
   * Fetches a list of integrations, optionally filtering by widget ID.
   * @param {string|null} widgetId - The UUID of the widget to filter integrations by (optional).
   * @returns {Promise} - Promise representing the list of integrations.
   */
  listIntegrations(widgetId = null) {
    const queryParams = []

    if (widgetId) {
      queryParams.push(`widget_id=${widgetId}`)
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : ''

    return session.get(`${APIEndpoint}/api/integration/${queryString}`)
  },

  /**
   * Creates a new integration.
   * @param {Object} integrationData - Data for the new integration.
   * @returns {Promise} - Promise representing the created integration.
   */
  createIntegration(integrationData) {
    if (!integrationData || typeof integrationData !== 'object') {
      throw new Error('Integration creation requires valid data.')
    }

    return session.post(`${APIEndpoint}/api/integration/`, integrationData)
  },

  /**
   * Updates an existing integration.
   * @param {Object} integrationData - Data for the integration to update, including the ID.
   * @returns {Promise} - Promise representing the updated integration.
   */
  updateIntegration(integrationData) {
    if (!integrationData || typeof integrationData !== 'object') {
      throw new Error('Integration update requires valid data.')
    }
    if (!integrationData.id) {
      throw new Error('Integration update requires an \'id\' field.')
    }

    return session.put(`${APIEndpoint}/api/integration/${integrationData.id}/`,
      integrationData)
  },

  /**
   * Deletes a integration by ID.
   * @param {Number|String} integrationId - The ID of the integration to delete.
   * @returns {Promise} - Promise representing the deletion operation.
   */
  deleteIntegration(integrationId) {
    if (!integrationId) {
      throw new Error('Integration deletion requires a valid ID.')
    }
    return session.delete(`${APIEndpoint}/api/integration/${integrationId}/`)
  },

  /**
   * Initiates the OAuth flow by fetching the authorization URL based on integration type.
   * @param {Object} data - Contains widget ID and integration type.
   * @param {String} data.widget - The widget ID.
   * @param {String} data.integration - The integration type (e.g., 'MAILCHIMP', 'AWEBER').
   * @returns {Promise} - Promise representing the authorization URL.
   */
  getOAuthAuthUrl({ widget, integration }) {
    const integrationType = integration.toUpperCase()

    const oauthEndpoints = {
      MAILCHIMP: this.getMailchimpAuthUrl,
      AWEBER: this.getAWeberAuthUrl,
      BREVO: this.getBrevoAuthUrl,
      // Add other OAuth integrations here...
    }

    const oauthMethod = oauthEndpoints[integrationType]

    if (!oauthMethod) {
      return Promise.reject(
        new Error(`Unsupported integration type: ${integrationType}`))
    }

    return oauthMethod.call(this, { widget })
  },

  /**
   * Initiates the Mailchimp OAuth flow by fetching the authorization URL.
   * @param {Object} data - Data required to initiate OAuth (e.g., widget).
   * @returns {Promise} - Promise representing the authorization URL.
   */
  getMailchimpAuthUrl(data) {
    return session.post(`${APIEndpoint}/api/mailchimp/authorize/`, data)
  },

  /**
   * Initiates the AWeber OAuth flow by fetching the authorization URL.
   * @param {Object} data - Data required to initiate OAuth (e.g., widget).
   * @returns {Promise} - Promise representing the authorization URL.
   */
  getAWeberAuthUrl(data) {
    return session.post(`${APIEndpoint}/api/aweber/auth-url/`, data)
  },

  getBrevoAuthUrl(data) {
    return session.post(`${APIEndpoint}/api/brevo/auth-url/`, data)
  },

  /**
   * Fetches lists associated with a specific integration.
   * @param {String} integrationType - The type of integration (e.g., 'MAILCHIMP').
   * @param {Number|String} integrationId - The ID of the integration.
   * @returns {Promise} - Promise representing the lists.
   */
  fetchLists(integrationType, integrationId) {
    if (!integrationId) {
      throw new Error(
        `Fetching lists requires a valid integration ID for ${integrationType}.`)
    }

    const fetchListsEndpoints = {
      MAILCHIMP: this.fetchMailchimpLists,
      AWEBER: this.fetchAWeberLists,
      ACTIVECAMPAIGN: this.fetchActiveCampaignLists,
      SENDGRID: this.fetchSendGridLists,
      GETRESPONSE: this.fetchGetResponseLists,
      SENDFOX: this.fetchSendFoxLists,
      KLAVIYO: this.fetchKlaviyoLists,
      // Add other integrations here...
    }

    const fetchListsMethod = fetchListsEndpoints[integrationType.toUpperCase()]

    if (!fetchListsMethod) {
      return Promise.reject(new Error(
        `Unsupported integration type for fetching lists: ${integrationType}`))
    }

    return fetchListsMethod.call(this, integrationId)
  },

  /**
   * Fetches merge/custom fields for a specific list associated with a specific integration.
   * @param {String} integrationType - The type of integration (e.g., 'MAILCHIMP').
   * @param {Number|String} integrationId - The ID of the integration.
   * @param {String} listId - The ID of the list.
   * @returns {Promise} - Promise representing the merge/custom fields.
   */
  fetchCustomFields(integrationType, integrationId, listId) {
    if (!integrationId || !listId) {
      throw new Error(
        `Fetching custom fields requires a valid integration ID and list ID for ${integrationType}.`)
    }

    const fetchFieldsEndpoints = {
      MAILCHIMP: this.fetchMailchimpMergeFields,
      AWEBER: this.fetchAWeberCustomFields,
      ACTIVECAMPAIGN: this.fetchActiveCampaignMergeFields,
      SENDGRID: this.fetchSendGridCustomFields,
      GETRESPONSE: this.fetchGetResponseCustomFields,
      KLAVIYO: this.fetchKlaviyoSpecialFields,
      // Add other integrations here...
    }

    const fetchFieldsMethod = fetchFieldsEndpoints[integrationType.toUpperCase()]

    if (!fetchFieldsMethod) {
      return Promise.reject(new Error(
        `Unsupported integration type for fetching custom fields: ${integrationType}`))
    }

    return fetchFieldsMethod.call(this, integrationId, listId)
  },

  /**
   * Fetches Mailchimp lists associated with a Mailchimp integration.
   * @param {Number|String} integrationId - The ID of the Mailchimp integration.
   * @returns {Promise} - Promise representing the list of Mailchimp lists.
   */
  fetchMailchimpLists(integrationId) {
    return session.get(`${APIEndpoint}/api/mailchimp/lists/`, {
      params: { integration_id: integrationId },
    })
  },

  /**
   * Fetches Mailchimp merge fields for a specific list associated with a Mailchimp integration.
   * @param {Number|String} integrationId - The ID of the Mailchimp integration.
   * @param {String} listId - The ID of the Mailchimp list.
   * @returns {Promise} - Promise representing the list of Mailchimp merge fields.
   */
  fetchMailchimpMergeFields(integrationId, listId) {
    return session.get(`${APIEndpoint}/api/mailchimp/merge-fields/`, {
      params: { integration_id: integrationId, list_id: listId },
    })
  },

  /**
   * Fetches AWeber lists associated with an AWeber integration.
   * @param {Number|String} integrationId - The ID of the AWeber integration.
   * @returns {Promise} - Promise representing the list of AWeber lists.
   */
  fetchAWeberLists(integrationId) {
    return session.get(`${APIEndpoint}/api/aweber/lists/`, {
      params: { integration_id: integrationId },
    })
  },

  /**
   * Fetches AWeber custom fields for a specific list associated with an AWeber integration.
   * @param {Number|String} integrationId - The ID of the AWeber integration.
   * @param {String} listId - The ID of the AWeber list.
   * @returns {Promise} - Promise representing the list of AWeber custom fields.
   */
  fetchAWeberCustomFields(integrationId, listId) {
    return session.get(`${APIEndpoint}/api/aweber/custom-fields/`, {
      params: { integration_id: integrationId, list_id: listId },
    })
  },

  /**
   * Fetches ActiveCampaign lists associated with an ActiveCampaign integration.
   * @param {Number|String} integrationId - The ID of the ActiveCampaign integration.
   * @returns {Promise} - Promise representing the list of ActiveCampaign lists.
   */
  fetchActiveCampaignLists(integrationId) {
    return session.get(`${APIEndpoint}/api/activecampaign/lists/`, {
      params: { integration_id: integrationId },
    })
  },

  /**
   * Fetches ActiveCampaign merge fields for a specific list associated with an ActiveCampaign integration.
   * @param {Number|String} integrationId - The ID of the ActiveCampaign integration.
   * @param {String} listId - The ID of the ActiveCampaign list.
   * @returns {Promise} - Promise representing the list of ActiveCampaign merge fields.
   */
  fetchActiveCampaignMergeFields(integrationId, listId) {
    return session.get(`${APIEndpoint}/api/activecampaign/merge-fields/`, {
      params: { integration_id: integrationId, list_id: listId },
    })
  },

  /**
   * Fetches SendGrid lists associated with a SendGrid integration.
   * @param {Number|String} integrationId - The ID of the SendGrid integration.
   * @returns {Promise} - Promise representing the list of SendGrid lists.
   */
  fetchSendGridLists(integrationId) {
    return session.get(`${APIEndpoint}/api/sendgrid/lists/`, {
      params: { integration_id: integrationId },
    })
  },

  /**
   * Fetches SendGrid custom fields for a specific list associated with a SendGrid integration.
   * @param {Number|String} integrationId - The ID of the SendGrid integration.
   * @param {String} listId - The ID of the SendGrid list.
   * @returns {Promise} - Promise representing the list of SendGrid custom fields.
   */
  fetchSendGridCustomFields(integrationId, listId) {
    return session.get(`${APIEndpoint}/api/sendgrid/custom-fields/`, {
      params: { integration_id: integrationId, list_id: listId },
    })
  },

  /**
   * Fetches GetResponse lists associated with a GetResponse integration.
   * @param {Number|String} integrationId - The ID of the GetResponse integration.
   * @returns {Promise} - Promise representing the list of GetResponse lists.
   */
  fetchGetResponseLists(integrationId) {
    return session.get(`${APIEndpoint}/api/getresponse/lists/`, {
      params: { integration_id: integrationId },
    })
  },

  /**
   * Fetches GetResponse custom fields for a specific list associated with a GetResponse integration.
   * @param {Number|String} integrationId - The ID of the GetResponse integration.
   * @param {String} listId - The ID of the GetResponse list.
   * @returns {Promise} - Promise representing the list of GetResponse custom fields.
   */
  fetchGetResponseCustomFields(integrationId, listId) {
    return session.get(`${APIEndpoint}/api/getresponse/custom-fields/`, {
      params: { integration_id: integrationId, list_id: listId },
    })
  },
  /**
   * Fetches SendFox lists associated with a SendFox integration.
   * @param {String} oauthToken - The OAuth token for SendFox API.
   * @returns {Promise} - Promise representing the list of SendFox lists.
   */
  fetchSendFoxLists(integrationId) {
    return session.get(`${APIEndpoint}/api/sendfox/lists/`, {
      params: { integration_id: integrationId },
    })
  },
  fetchSendFoxCustomFields(integrationId, listId) {
    return session.get(`${APIEndpoint}/api/sendfox/custom-fields/`, {
      params: { integration_id: integrationId, list_id: listId },
    })
  },
  /**
   * Fetches Klaviyo lists associated with a Klaviyo integration.
   * @param {Number|String} integrationId - The ID of the Klaviyo integration.
   * @returns {Promise} - Promise representing the list of Klaviyo lists.
   */
  fetchKlaviyoLists(integrationId) {
    return session.get(`${APIEndpoint}/api/klaviyo/lists/`, {
      params: { integration_id: integrationId },
    })
  },

  /**
   * Fetches Klaviyo special fields for a specific list associated with a Klaviyo integration.
   * @param {Number|String} integrationId - The ID of the Klaviyo integration.
   * @param {String} listId - The ID of the Klaviyo list.
   * @returns {Promise} - Promise representing the list of Klaviyo special fields.
   */
  fetchKlaviyoSpecialFields(integrationId, listId) {
    return session.get(`${APIEndpoint}/api/klaviyo/special-fields/`, {
      params: { integration_id: integrationId, list_id: listId },
    })
  },
  fetchBrevoLists(integrationId) {
    return session.get(`${APIEndpoint}/api/brevo/lists/`, {
      params: { integration_id: integrationId },
    })
  },
  fetchBrevoCustomFields(integrationId, listId) {
    return session.get(`${APIEndpoint}/api/brevo/custom-fields/`, {
      params: { integration_id: integrationId, list_id: listId },
    })
  },
  /**
   * Fetches Omnisend lists/segments associated with an Omnisend integration.
   * @param {Number|String} integrationId - The ID of the Omnisend integration.
   * @returns {Promise} - Promise representing the list of Omnisend lists/segments.
   */
  fetchOmnisendLists(integrationId) {
    return session.get(`${APIEndpoint}/api/omnisend/lists/`, {
      params: { integration_id: integrationId },
    })
  },

  /**
   * Fetches Omnisend special fields for a specific list associated with an Omnisend integration.
   * @param {Number|String} integrationId - The ID of the Omnisend integration.
   * @param {String} listId - The ID of the Omnisend list/segment.
   * @returns {Promise} - Promise representing the list of Omnisend special fields.
   */
  fetchOmnisendSpecialFields(integrationId, listId) {
    return session.get(`${APIEndpoint}/api/omnisend/special-fields/`, {
      params: { integration_id: integrationId, list_id: listId },
    })
  },
  /**
   * Fetches Mailjet lists associated with a Mailjet integration.
   * @param {Number|String} integrationId - The ID of the Mailjet integration.
   * @returns {Promise} - Promise representing the list of Mailjet contact lists.
   */
  fetchMailjetLists(integrationId) {
    return session.get(`${APIEndpoint}/api/mailjet/lists/`, {
      params: { integration_id: integrationId },
    })
  },

  /**
   * Fetches Mailjet contact properties for a specific list (optional) associated with a Mailjet integration.
   * @param {Number|String} integrationId - The ID of the Mailjet integration.
   * @param {String} listId - The ID of the Mailjet list (if applicable).
   * @returns {Promise} - Promise representing the list of Mailjet contact properties.
   */
  fetchMailjetContactProperties(integrationId, listId) {
    return session.get(`${APIEndpoint}/api/mailjet/contact-properties/`, {
      params: { integration_id: integrationId, list_id: listId },
    })
  },
  /**
   * Fetches Moosend lists associated with a Moosend integration.
   * @param {Number|String} integrationId - The ID of the Moosend integration.
   * @returns {Promise} - Promise representing the list of Moosend contact lists.
   */
  fetchMoosendLists(integrationId) {
    return session.get(`${APIEndpoint}/api/moosend/lists/`, {
      params: { integration_id: integrationId },
    })
  },

  /**
   * Fetches Moosend contact properties for a specific list (optional) associated with a Moosend integration.
   * @param {Number|String} integrationId - The ID of the Moosend integration.
   * @param {String} listId - The ID of the Moosend list (if applicable).
   * @returns {Promise} - Promise representing the list of Moosend contact properties.
   */
  fetchMoosendCustomFields(integrationId, listId) {
    return session.get(`${APIEndpoint}/api/moosend/custom-fields/`, {
      params: { integration_id: integrationId, list_id: listId },
    })
  },
  /**
   * Fetches MailerLite groups associated with a MailerLite integration.
   * @param {Number|String} integrationId - The ID of the MailerLite integration.
   * @returns {Promise} - Promise representing the list of MailerLite groups.
   */
  fetchMailerLiteLists(integrationId) {
    return session.get(`${APIEndpoint}/api/mailerlite/lists/`, {
      params: { integration_id: integrationId },
    })
  },

  /**
   * Fetches MailerLite custom fields for a specific group associated with a MailerLite integration.
   * @param {Number|String} integrationId - The ID of the MailerLite integration.
   * @param {String} groupId - The ID of the MailerLite group.
   * @returns {Promise} - Promise representing the list of MailerLite custom fields.
   */
  fetchMailerLiteCustomFields(integrationId, groupId) {
    return session.get(`${APIEndpoint}/api/mailerlite/custom-fields/`, {
      params: { integration_id: integrationId, group_id: groupId },
    })
  },
}